import { useState } from "react";
import axios from "axios";
import { useParams } from "react-router";
import { API_BASE_URL, API_URLS, LOCALSTORAGE_TOKEN_ITEM } from "constants/api";
import { bytesToHuman, isPerfDebugEnabled, perfLog, perfNow } from "utils/perfDebug";

interface Track {
  id: string;
  title: string;
  duration: number;
  artist: string;
  has_audio: any;
}

type TracksCacheEntry = { ts: number; tracks: any[] };
const TRACKS_CACHE_MS = 60_000; // 60s is enough to make UI feel instant while still staying fresh
const tracksCache = new Map<string, TracksCacheEntry>();
const inflightTracks = new Map<string, Promise<any[]>>();
const storageKey = (releaseId: string) => `nr_tracks_cache:${releaseId}`;

export const useTracks = (releaseIdFromProps?: string) => {
  const [tracks, setTracks] = useState<Track[]>([]);
  const [audio, setAudio] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);

  const token = localStorage.getItem(LOCALSTORAGE_TOKEN_ITEM);
  const params = useParams<{ id: string }>();
  const releaseId = String(releaseIdFromProps || params.id);

  const authHeaders = {
    Accept: "application/json",
    Authorization: `Bearer ${token}`,
  };

  const debugFormData = (data: FormData) => {
    if (process.env.NODE_ENV === "production") return;
    try {
      const keys: string[] = [];
      const files: Array<{ key: string; name: string; size: number; type: string }> = [];
      data.forEach((v, k) => {
        keys.push(k);
        if (v instanceof File) files.push({ key: k, name: v.name, size: v.size, type: v.type });
      });
      console.log("[useTracks] FormData keys", { keys, files });
    } catch {
      // ignore
    }
  };

  

  const getTracks = async (
    ensureTrack?: { id?: string | number } | null,
    opts?: { backgroundRefresh?: boolean }
  ) => {
    try {
      setUpdating(true);

      const perf = isPerfDebugEnabled();
      const t0 = perf ? perfNow() : 0;

      const cacheKey = String(releaseId);
      // Hydrate from sessionStorage (helps after refresh / step re-entry)
      if (!tracksCache.has(cacheKey)) {
        try {
          const raw = sessionStorage.getItem(storageKey(cacheKey));
          if (raw) {
            const parsed = JSON.parse(raw) as TracksCacheEntry;
            if (parsed?.ts && Array.isArray(parsed.tracks)) {
              tracksCache.set(cacheKey, parsed);
            }
          }
        } catch {
          // ignore
        }
      }
      const cached = tracksCache.get(cacheKey);
      const now = Date.now();
      const isCacheFresh = cached && now - cached.ts < TRACKS_CACHE_MS;

      // If asked, return cached data immediately (perceived perf), then refresh in background.
      if (opts?.backgroundRefresh && isCacheFresh) {
        setTracks(cached!.tracks as any);
        if (perf) {
          perfLog("NewRelease.getTracks.cacheHit", {
            releaseId,
            ageMs: now - cached!.ts,
            count: cached!.tracks?.length,
          });
        }

        // kick background refresh (deduped)
        if (!inflightTracks.has(cacheKey)) {
          const p = axios
            .get(`${API_BASE_URL}${API_URLS.ReleaseTracks.replace("{release}", releaseId)}`, { headers: authHeaders })
            .then((res) => {
              const fresh = JSON.parse(JSON.stringify(res.data.data || []));
              const entry = { ts: Date.now(), tracks: fresh };
              tracksCache.set(cacheKey, entry);
              try {
                sessionStorage.setItem(storageKey(cacheKey), JSON.stringify(entry));
              } catch {
                // ignore
              }
              setTracks(fresh as any);
              if (perf) {
                perfLog("NewRelease.getTracks.backgroundRefresh.done", {
                  releaseId,
                  count: Array.isArray(fresh) ? fresh.length : undefined,
                });
              }
              return fresh;
            })
            .finally(() => {
              inflightTracks.delete(cacheKey);
            });
          inflightTracks.set(cacheKey, p);
        }

        return cached!.tracks as any[];
      }

      if (process.env.NODE_ENV !== "production") {
        console.log("[useTracks.getTracks] GET", {
          releaseId,
          url: `${API_BASE_URL}${API_URLS.ReleaseTracks.replace("{release}", releaseId)}`,
        });
        console.time("[useTracks.getTracks] time");
      }
      // Deduplicate concurrent calls
      if (inflightTracks.has(cacheKey)) {
        const existing = inflightTracks.get(cacheKey)!;
        const next = await existing;
        if (perf) {
          perfLog("NewRelease.getTracks.inflightJoin", { releaseId, ms: Math.round(perfNow() - t0) });
        }
        setTracks(next as any);
        return next as any[];
      }

      const promise = axios
        .get(`${API_BASE_URL}${API_URLS.ReleaseTracks.replace("{release}", releaseId)}`, { headers: authHeaders })
        .then((res) => {
          if (perf) {
            // Approximate payload size (JSON) without relying on Content-Length
            let approxBytes = 0;
            try {
              const json = JSON.stringify(res.data);
              approxBytes =
                typeof TextEncoder !== "undefined" ? new TextEncoder().encode(json).byteLength : json.length;
            } catch {
              approxBytes = 0;
            }
            perfLog("NewRelease.getTracks.done", {
              releaseId,
              ms: Math.round(perfNow() - t0),
              approxPayload: bytesToHuman(approxBytes),
              count: Array.isArray(res.data?.data) ? res.data.data.length : undefined,
              status: res.status,
            });
          }
      const fresh = JSON.parse(JSON.stringify(res.data.data || []));   
          const entry = { ts: Date.now(), tracks: fresh };
          tracksCache.set(cacheKey, entry);
          try {
            sessionStorage.setItem(storageKey(cacheKey), JSON.stringify(entry));
          } catch {
            // ignore
          }
          return fresh;
        })
        .finally(() => {
          inflightTracks.delete(cacheKey);
        });
      inflightTracks.set(cacheKey, promise);
      const fresh = await promise;
      
      // Some backends are eventually consistent; ensure the just-created track is present in UI.
      const ensureId = ensureTrack?.id != null ? String(ensureTrack.id) : null;
      const hasEnsure = ensureId
        ? fresh.some((t: any) => String(t?.id) === ensureId)
        : true;
      const next = !hasEnsure && ensureTrack ? [...fresh, ensureTrack] : fresh;
      setTracks(next as any);
      if (process.env.NODE_ENV !== "production") {
        console.timeEnd("[useTracks.getTracks] time");
        console.log("[useTracks.getTracks] OK", { count: next.length });
      }
      return next;

    } catch (err) {
      console.error("Failed to fetch tracks:", err);
      return tracks;
    } finally {
      setUpdating(false);
    }
  };

  const upDateRole = async (
    trackId: number,
    artistId: number,
    role: string,
    opts?: { refresh?: boolean }
  ) => {
    if (!releaseId || !artistId || !role) return;

    try {
      setUpdating(true);
      const response = await axios.post(
        `${API_BASE_URL}${API_URLS.TracksRole
          .replace("{track}", String(trackId))
          .replace("{artist}", String(artistId))}`,
        { role },
        { headers: { Accept: "application/json", Authorization: `Bearer ${token}` } }
      );

      if (opts?.refresh !== false) {
        await getTracks();
      }
    } catch (error) {
      console.error("Update role error:", error);
    } finally {
      setUpdating(false);
    }
  };

  const getAudio = async (
    trackId: string,
    opts?: { signal?: AbortSignal }
  ): Promise<Blob> => {
    try {
      setLoading(true);

      const perf = isPerfDebugEnabled();
      const t0 = perf ? perfNow() : 0;
      if (perf) {
        perfLog("NewRelease.getAudio.start", { releaseId, trackId });
      }
      const response = await axios.get(
        `${API_BASE_URL}${API_URLS.audio.replace("{track}", trackId)}`,
        {
          headers: authHeaders,
          responseType: "blob",
          signal: opts?.signal,
        }
      );

      if (perf) {
        const bytes = (response?.data as any)?.size ?? 0;
        const contentLength =
          (response.headers?.["content-length"] ?? response.headers?.["Content-Length"]) as string | undefined;
        const contentType =
          (response.headers?.["content-type"] ?? response.headers?.["Content-Type"]) as string | undefined;
        perfLog("NewRelease.getAudio.done", {
          releaseId,
          trackId,
          ms: Math.round(perfNow() - t0),
          bytes: bytesToHuman(bytes),
          contentLengthHeader: contentLength,
          contentTypeHeader: contentType,
        });
      }

      return response.data;
    } finally {
      setLoading(false);
    }
  };


  const addTrack = async (trackId: string, data: FormData) => {
    if (!trackId) return;

    try {
      setUpdating(true);
      const url = `${API_BASE_URL}${API_URLS.ReleaseTracks
        .replace("{release}", releaseId)
        .replace("{track}", trackId)}`;
      const perf = isPerfDebugEnabled();
      const t0 = perf ? perfNow() : 0;
      if (process.env.NODE_ENV !== "production") {
        console.log("[useTracks.addTrack] POST", { releaseId, trackId, url });
        debugFormData(data);
        console.time("[useTracks.addTrack] POST time");
      }
      const res = await axios.post(
        url,
        data,
        {
          headers: {
            ...authHeaders,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      const created = res.data.data;
      setTracks(prev => [...prev, created]);
      if (perf) {
        perfLog("NewRelease.addTrack.done", {
          releaseId,
          ms: Math.round(perfNow() - t0),
          createdId: created?.id,
        });
      }
      if (process.env.NODE_ENV !== "production") {
        console.timeEnd("[useTracks.addTrack] POST time");
        console.log("[useTracks.addTrack] OK", { status: res.status, id: res.data?.data?.id });
      }
      return created;

    } catch (err) {
      console.error("Error adding track:", err);
      if (process.env.NODE_ENV !== "production" && axios.isAxiosError(err)) {
        console.log("[useTracks.addTrack] ERR", {
          status: err.response?.status,
          errors: (err.response?.data as any)?.errors,
          message: (err.response?.data as any)?.message,
        });
      }
      throw err;
    } finally {
      setUpdating(false);
    }
  };

 
  const updateTrack = async (trackId: string, data: FormData) => {
    if (!trackId) return;

    try {
      setUpdating(true);
      const url = `${API_BASE_URL}${API_URLS.TrackDelete
        .replace("{release}", releaseId)
        .replace("{track}", trackId)}`;
      const perf = isPerfDebugEnabled();
      const t0 = perf ? perfNow() : 0;
      if (process.env.NODE_ENV !== "production") {
        console.log("[useTracks.updateTrack] POST", { releaseId, trackId, url });
        debugFormData(data);
      }

      const res = await axios.post(
        url,
        data,
        {
          headers: {
            ...authHeaders,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      const updated = JSON.parse(JSON.stringify(res.data.data));

      setTracks(prev =>
        prev.map(t =>
          String(t.id) === String(trackId) ? { ...t, ...updated } : t
        )
      );
      if (process.env.NODE_ENV !== "production") {
        console.log("[useTracks.updateTrack] OK", { status: res.status, id: res.data?.data?.id });
      }
      if (perf) {
        perfLog("NewRelease.updateTrack.done", {
          releaseId,
          trackId,
          ms: Math.round(perfNow() - t0),
        });
      }
      return updated;

    } catch (err) {
      console.error("Error updating track:", err);
      throw err;
    } finally {
      setUpdating(false);
    }
  };

  const deleteTrack = async (trackId: string) => {
    try {
      setUpdating(true);

      await axios.delete(
        `${API_BASE_URL}${API_URLS.TrackDelete
          .replace("{release}", releaseId)
          .replace("{track}", trackId)}`,
        { headers: authHeaders }
      );

      // Remove from state
      // setTracks(prev =>
      //   prev.filter(t => String(t.id) !== String(trackId))
      // );
      await getTracks()

    } catch (err) {
      console.error("Error deleting track:", err);
    } finally {
      setUpdating(false);
    }
  };

  const reorderTracksArtists = async (trackId: number, data: any) => {
    try {
      setUpdating(true);

      await axios.patch(
        `${API_BASE_URL}${API_URLS.TracksArtistsReorder.replace(
          "{track}",
          String(trackId)
        )}`,
        data,
        { headers: authHeaders }
      );

      await getTracks(); 

    } finally {
      setUpdating(false);
    }
  };

  const reorderReleaseTracks = async (releaseId: number, data: any) => {
    try {
      setUpdating(true);

      await axios.patch(
        `${API_BASE_URL}${API_URLS.TracksReorder.replace(
          "{release}",
          String(releaseId)
        )}`,
        data,
        { headers: authHeaders }
      );

      await getTracks(); 

    } finally {
      setUpdating(false);
    }
  };

  return {
    tracks,
    audio,
    setAudio,
    loading,
    updating,
    getTracks,
    getAudio,
    addTrack,
    updateTrack,
    deleteTrack,
    reorderTracksArtists,
    reorderReleaseTracks,
    upDateRole
    
  };
};
